import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';
import { Menu, X, Instagram, Facebook } from 'lucide-react';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { key: 'home', id: 'home' },
    { key: 'about', id: 'about' },
    { key: 'products', id: 'products' },
    { key: 'customMix', id: 'custom-mix' },
    { key: 'portfolio', id: 'portfolio' },
    { key: 'contact', id: 'contact' }
  ];
  // "Novidades" NAO e' uma pagina propria — essa foi descartada (correcao da
  // Licinia, 23/09/2026): e' a seccao <Novidades /> (componente proprio,
  // Novidades.jsx), renderizada em App.js ENTRE <About /> e <Products />.
  // Por isso faz scroll como os itens de cima, so' que entra ANTES de
  // "Produtos" — mesma ordem do menu. Quem clica nisto vindo de outra pagina
  // (paginas estaticas) usa href="/#novidades" no proprio HTML dessas
  // paginas; o handler de hash do App.js trata o resto.
  const indiceProdutos = navItems.findIndex((item) => item.id === 'products');
  const navAntesNovidades = navItems.slice(0, indiceProdutos);
  const navDepoisNovidades = navItems.slice(indiceProdutos);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src={companyInfo.logo}
              alt="Pigmentos"
              className="h-14 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navAntesNovidades.map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors duration-200 hover:text-orange-600 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}

            <button
              onClick={() => scrollToSection('novidades')}
              className={`text-sm font-medium transition-colors duration-200 hover:text-orange-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {language === 'en' ? "What's new" : 'Novidades'}
            </button>

            {navDepoisNovidades.map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors duration-200 hover:text-orange-600 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}

            {/* Guias — leva ao ÍNDICE /guias/, não a uma guia concreta.
                Já apontou direto para /tinta-para-telhados/, e enquanto só havia uma guia
                não fazia diferença. Com três, quem clicava caía numa delas em vez da lista,
                e as outras ficavam sem entrada em menu nenhum. Guia nova entra em
                _scripts/gerar_pagina_guias.py e aparece aqui sozinha. */}
            <a
              href="/guias/"
              className={`text-sm font-medium transition-colors duration-200 hover:text-orange-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {language === 'en' ? 'Guides' : 'Guias'}
            </a>
          </nav>

          {/* Right Side - Social, Language, Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Social Icons */}
            <div className="hidden md:flex items-center space-x-3">
              <a
                href={companyInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors duration-200 hover:text-orange-600 ${
                  isScrolled ? 'text-gray-600' : 'text-white'
                }`}
              >
                <Instagram size={18} />
              </a>
              <a
                href={companyInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors duration-200 hover:text-orange-600 ${
                  isScrolled ? 'text-gray-600' : 'text-white'
                }`}
              >
                <Facebook size={18} />
              </a>
            </div>

            {/* Language Selector */}
            <div className="flex items-center space-x-1 text-sm">
              <button
                onClick={() => toggleLanguage('pt')}
                className={`px-2 py-1 rounded transition-all duration-200 ${
                  language === 'pt'
                    ? 'bg-orange-600 text-white'
                    : isScrolled
                    ? 'text-gray-600 hover:bg-gray-100'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                PT 🇵🇹
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>|</span>
              <button
                onClick={() => toggleLanguage('en')}
                className={`px-2 py-1 rounded transition-all duration-200 ${
                  language === 'en'
                    ? 'bg-orange-600 text-white'
                    : isScrolled
                    ? 'text-gray-600 hover:bg-gray-100'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                EN 🇬🇧
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2 rounded-lg transition-colors duration-200 ${
                isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen bg-white shadow-xl' : 'max-h-0'
        }`}
      >
        <nav className="px-4 py-4 space-y-2">
          {navAntesNovidades.map((item) => (
            <button
              key={item.key}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors duration-200"
            >
              {t(`nav.${item.key}`)}
            </button>
          ))}
          <button
            onClick={() => scrollToSection('novidades')}
            className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors duration-200"
          >
            {language === 'en' ? "What's new" : 'Novidades'}
          </button>
          {navDepoisNovidades.map((item) => (
            <button
              key={item.key}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors duration-200"
            >
              {t(`nav.${item.key}`)}
            </button>
          ))}
          <a
            href="/guias/"
            className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors duration-200"
          >
            {language === 'en' ? 'Guides' : 'Guias'}
          </a>
          <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-100">
            <a
              href={companyInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-orange-600 transition-colors"
            >
              <Instagram size={22} />
            </a>
            <a
              href={companyInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-orange-600 transition-colors"
            >
              <Facebook size={22} />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
